import React, { ReactElement, useEffect, useState } from 'react';
import { useIdentityContext } from '@design-stack-vista/identity-provider';
import { Box, Button, FlexBox, Spinner, Typography } from '@vp/swan';
import { isWorkOwnedByUser } from '@vp/upload-and-print-validator';
import { useDebugContext } from 'src/hooks/DebugContextHooks';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { text } from 'src/utils/localizationUtil';
import { noticeError } from 'src/utils/newRelicUtil';
import QueryStringUtil from 'src/utils/QueryStringUtil';
import { getTenant } from 'src/utils/tenantUtil';

/**
 * Sign in if editing an existing work and the user is anonymous but does not own the work.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function signInIfNecessary(vpAuth: any, setError: (error: unknown) => void) {
  try {
    if (!vpAuth.isSignedIn()) {
      const workId = QueryStringUtil.getValueByKey('workId');
      if (workId) {
        const ownsWork = await isWorkOwnedByUser(workId, getTenant(), getAuthHeader());
        if (!ownsWork) {
          vpAuth.signIn();
        }
      }
    }
  } catch (e) {
    console.error('Error signing in if necessary', e);
    noticeError(e, {
      method: 'signInIfNecessary',
    });
    setError(e);
  }
}

export type PreUploadedFileProcessorProps = {
  isMobile: boolean;
};

export const PreUploadedFileProcessor = ({ isMobile }: PreUploadedFileProcessorProps): ReactElement => {
  const { auth: vpAuth } = useIdentityContext();
  const { upload } = useUploadContext();
  const { processUpload, reprocessUpload } = useProcessUpload();
  const { isValidating } = useValidationContext();
  const [processingStarted, setProcessingStarted] = useState(false);
  const { setError } = useErrorContext();
  const { debugMode } = useDebugContext();

  useEffect(() => {
    (async function() {
      if (vpAuth && upload && !processingStarted) {
        setProcessingStarted(true);
        await signInIfNecessary(vpAuth, setError);
        await processUpload(upload);
      }
    })();
  }, [vpAuth, upload, processingStarted, processUpload, setError]);

  return (
    <>
      {isValidating && (
        <Box
          marginBottom={isMobile ? '7' : '8'}
          backgroundColor="strong"
          padding="7"
          style={{
            height: 191,
            borderRadius: 8,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'var(--swan-base-border-color)',
            overflow: 'visible',
            position: 'relative',
            maxWidth: 450,
          }}
        >
          <FlexBox
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <FlexBox
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              style={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <>
                <FlexBox justifyContent="center" alignItems="center">
                  <Spinner accessibleText={text('loading')} size="standard" />
                </FlexBox>
                {isValidating && (
                  <Typography marginTop={'6'} fontWeight="bold" fontSize={'standard'}>
                    {text('processing-design')}
                  </Typography>
                )}
                <Typography fontSize={'xsmall'} textColor="subtle">
                  {text('processing-secondary')}
                </Typography>
              </>
            </FlexBox>
          </FlexBox>
        </Box>
      )}
      {debugMode && <Button onClick={reprocessUpload}>Reprocess Upload</Button>}
    </>
  );
};
